import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, UserCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const LANG_STYLE = {
  arabic: "bg-emerald-50 text-emerald-700 border-emerald-200",
  english: "bg-sky-50 text-sky-700 border-sky-200",
};

/** Shared reassignment picker — single lead (detail page) or bulk (list). */
export default function ReassignDialog({ open, onOpenChange, leadIds, arabicHint = false, onDone }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(null); setQ(""); setReason("");
    api.get("/leads/assignable-users")
      .then((r) => setUsers(r.data.users || []))
      .catch(() => toast.error("Failed to load users"));
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) => u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s));
  }, [users, q]);

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = await api.post("/leads/assign", {
        lead_ids: leadIds,
        assign_to: picked.email,
        reason: reason.trim() || "manual_reassign",
      });
      const skipped = (data.skipped || []).length;
      toast.success(
        `${data.assigned_count} lead${data.assigned_count === 1 ? "" : "s"} reassigned to ${picked.name}` +
        (skipped ? ` · ${skipped} skipped (already assigned)` : "")
      );
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassignment failed");
    } finally {
      setBusy(false);
    }
  };

  const arabicMismatch = arabicHint && picked && !(picked.languages || []).includes("arabic");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            Reassign {leadIds.length} lead{leadIds.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Both the previous and new assignee will be notified. Logged in Assignment History.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email"
                 className="pl-8 h-8 text-sm" data-testid="reassign-user-search" />
        </div>

        <div className="max-h-56 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
          {filtered.length === 0 && (
            <div className="text-xs text-slate-400 text-center py-6">No matching users</div>
          )}
          {filtered.map((u) => (
            <button
              key={u.email}
              type="button"
              onClick={() => setPicked(u)}
              data-testid={`reassign-user-${u.email}`}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors ${
                picked?.email === u.email ? "bg-indigo-50/60" : ""
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate">{u.name}</div>
                <div className="text-[10px] text-slate-400 truncate">{u.email}</div>
              </div>
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border shrink-0 ${
                (u.open_leads || 0) === 0 ? "bg-slate-50 text-slate-400 border-slate-200"
                : (u.open_leads || 0) >= 50 ? "bg-rose-50 text-rose-700 border-rose-200"
                : "bg-slate-50 text-slate-600 border-slate-200"
              }`} title="Open leads currently assigned"
                    data-testid={`workload-${u.email}`}>
                {u.open_leads || 0} open
              </span>
              <Badge variant="outline" className="text-[9px] shrink-0">{u.role}</Badge>
              {(u.languages || []).map((l) => (
                <Badge key={l} variant="outline"
                       className={`text-[9px] uppercase shrink-0 ${LANG_STYLE[l] || ""}`}>
                  {l.slice(0, 2)}
                </Badge>
              ))}
              {picked?.email === u.email && <UserCheck className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
            </button>
          ))}
        </div>

        {arabicMismatch && (
          <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-2"
               data-testid="reassign-arabic-warning">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            This lead requires Arabic, but {picked.name} doesn't have Arabic listed. You can still proceed.
          </div>
        )}

        <Input value={reason} onChange={(e) => setReason(e.target.value)}
               placeholder="Reason (optional)" className="h-8 text-sm" data-testid="reassign-reason" />

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="reassign-cancel">
            Cancel
          </Button>
          <Button size="sm" disabled={!picked || busy} onClick={submit}
                  className="bg-indigo-600 hover:bg-indigo-700" data-testid="reassign-confirm">
            {busy ? "Reassigning…" : `Reassign${picked ? ` to ${picked.name.split(" ")[0]}` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
